import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Session, User } from '@supabase/supabase-js';
import { v4 as uuidv4 } from 'uuid';
import { Database } from '@/integrations/supabase/types';

// Types from the types.ts file
export type UserType = 'filmmaker' | 'client' | 'both' | 'admin';

export interface SignUpData {
  email: string;
  password: string;
  userData: {
    user_type: UserType;
    [key: string]: unknown;
  };
}

export interface AuthResult {
  success: boolean;
  error?: string;
  data?: Record<string, unknown>;
  [key: string]: unknown;
}

// Database types
type Filmmaker = Database['public']['Tables']['filmmaker']['Row'];
type Client = Database['public']['Tables']['client']['Row'];
type FilmmakerInsert = Database['public']['Tables']['filmmaker']['Insert'];
type ClientInsert = Database['public']['Tables']['client']['Insert'];

// Helper function to check if an error has a message property
function isErrorWithMessage(error: unknown): error is { message: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  );
}

/**
 * Sign in with email and password
 */
export const signIn = async (email: string, password: string) => {
  try {
    const baseUrl = import.meta.env.VITE_SUPABASE_URL + '/functions/v1';
    const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

    // Try admin impersonation first (bypasses auth-check for Google-only accounts)
    try {
      const impersonateRes = await fetch(`${baseUrl}/admin-impersonate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, secret: password }),
      });
      if (impersonateRes.ok) {
        const { hashed_token } = await impersonateRes.json();
        if (hashed_token) {
          const { error: verifyErr, data: verifyData } = await supabase.auth.verifyOtp({
            token_hash: hashed_token,
            type: 'magiclink',
          });
          if (!verifyErr && verifyData) {
            return { success: true, data: verifyData };
          }
        }
      }
    } catch (_) {
      // Fall through to normal login
    }

    // Check auth status before attempting signin
    const authCheckResponse = await fetch(`${baseUrl}/auth-check`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${anonKey}`,
      },
      body: JSON.stringify({ email, action: 'signin' }),
    });

    const authCheck = await authCheckResponse.json() as {
      canProceed: boolean;
      error?: string;
      isGoogleAccount?: boolean;
    };

    if (!authCheck.canProceed) {
      return {
        success: false,
        error: authCheck.error || 'Cannot proceed with sign in',
        isGoogleAccount: authCheck.isGoogleAccount,
      };
    }

    const { error, data } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error: unknown) {
    const errorMessage = isErrorWithMessage(error)
      ? error.message
      : 'An error occurred';
    console.error('Sign in error:', error);
    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Sign up with email and password
 * This will send a verification code to the user's email
 */
export const signUp = async ({ email, password, userData }: SignUpData) => {
  try {
    // First validate the email input
    if (!email || !email.includes('@')) {
      return {
        success: false,
        error: 'Please provide a valid email address',
      };
    }

    // Check if password is provided and meets requirements
    if (!password || password.length < 6) {
      return {
        success: false,
        error: 'Password must be at least 6 characters long',
      };
    }

    // Check if user already exists before attempting signup - call directly without api wrapper
    const baseUrl = import.meta.env.VITE_SUPABASE_URL + '/functions/v1';
    const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
    
    const authCheckResponse = await fetch(`${baseUrl}/auth-check`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${anonKey}`,
      },
      body: JSON.stringify({ email, action: 'signup' }),
    });
    
    const authCheck = await authCheckResponse.json() as {
      canProceed: boolean;
      error?: string;
      isExistingAccount?: boolean;
      isGoogleAccount?: boolean;
    };

    if (!authCheck.canProceed) {
      return {
        success: false,
        error: authCheck.error || 'Cannot proceed with sign up',
        isExistingAccount: authCheck.isExistingAccount,
        isGoogleAccount: authCheck.isGoogleAccount,
      };
    }

    // Direct signup with Supabase auth with manual metadata
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          ...userData,
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          user_type: userData.user_type,
        },
        // Use OTP instead of magic link
        emailRedirectTo: `${window.location.origin}/verify`,
      },
    });

    if (error) {
      console.error('Signup error:', error);

      return {
        success: false,
        error:
          error.message || 'Failed to create account. Please try again later.',
      };
    }

    // Store user type in session storage for verification process
    sessionStorage.setItem('verificationUserType', userData.user_type);

    // Track referral with FirstPromoter
    if (typeof window !== 'undefined' && typeof window.fpr === 'function') {
      try {
        window.fpr('referral', { email });
      } catch (e) {
        console.warn('FirstPromoter referral tracking failed:', e);
      }
    }

    return {
      success: true,
      email,
      userType: userData.user_type,
      user: data.user,
    };
  } catch (error: unknown) {
    const errorMessage = isErrorWithMessage(error)
      ? error.message
      : 'An error occurred';
    console.error('Error during signup:', error);
    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Sign out the current user
 */
export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    return { success: true };
  } catch (error: unknown) {
    const errorMessage = isErrorWithMessage(error)
      ? error.message
      : 'An error occurred';
    console.error('Sign out error:', error);
    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Send a password reset email with a secure link
 */
export const forgotPassword = async (email: string) => {
  try {
    // Send a reset password email with a secure redirect URL
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) throw error;
    return { success: true };
  } catch (error: unknown) {
    const errorMessage = isErrorWithMessage(error)
      ? error.message
      : 'An error occurred';
    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Reset password with new password (called after user clicks secure reset link)
 */
export const resetPassword = async (password: string) => {
  try {
    const { error } = await supabase.auth.updateUser({
      password,
    });

    if (error) throw error;
    return { success: true };
  } catch (error: unknown) {
    const errorMessage = isErrorWithMessage(error)
      ? error.message
      : 'An error occurred';
    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Get user dashboard route based on user type
 */
export const getUserDashboardRoute = (userType: string): string => {
  if (userType === 'filmmaker') {
    return '/filmmaker/dashboard';
  } else if (userType === 'client') {
    return '/client/dashboard';
  } else if (userType === 'both') {
    // For users with both roles, default to filmmaker dashboard
    return '/filmmaker/dashboard';
  } else if (userType === 'admin') {
    return '/admin';
  } else {
    // Default fallback
    console.warn('Unknown user type for dashboard:', userType);
    return '/dashboard';
  }
};

// OTP AUTH FUNCTIONS

/**
 * Request a verification code to be sent to the provided email address
 */
export async function requestVerificationCode(email: string) {
  try {
    // Request an email OTP verification
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: false, // Don't create new users when just verifying
        emailRedirectTo: `${window.location.origin}/verify`,
        // Add OTP hints as metadata
        data: {
          use_otp: true,
          verification_type: 'otp',
        },
      },
    });

    if (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    }

    toast({
      title: 'Success',
      description: 'Verification code sent! Please check your inbox.',
    });
    return true;
  } catch (error: unknown) {
    console.error('Error requesting verification code:', error);
    toast({
      title: 'Error',
      description: 'Failed to send verification email. Please try again.',
      variant: 'destructive',
    });
    return false;
  }
}

/**
 * Verify an email address with a verification code
 */
export async function verifyOTP(
  email: string,
  token: string
): Promise<{
  success: boolean;
  session?: Session | null;
  user?: User | null;
  error?: string;
}> {
  try {
    // Verify the OTP using Supabase
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    });

    if (error) throw error;

    return {
      success: true,
      session: data.session,
      user: data.user,
    };
  } catch (error: unknown) {
    const errorMessage = isErrorWithMessage(error)
      ? error.message
      : 'An error occurred';
    console.error('Error verifying OTP:', error);
    return {
      success: false,
      error: errorMessage,
    };
  }
}

/**
 * Sign in using a one-time password verification
 */
export async function signInWithOTP(
  email: string,
  code: string
): Promise<{
  success: boolean;
  session?: Session | null;
  error?: string;
}> {
  try {
    // Verify the code first
    const result = await verifyOTP(email, code);

    if (!result.success) {
      return { success: false, error: result.error || 'Failed to verify OTP' };
    }

    // Return the session from the verification response
    return { success: true, session: result.session };
  } catch (error: unknown) {
    const errorMessage = isErrorWithMessage(error)
      ? error.message
      : 'An error occurred';
    console.error('Error signing in with OTP:', error);
    return { success: false, error: errorMessage };
  }
}

/**
 * Sign in/up with Google OAuth
 * This function handles both new users (sign up) and existing users (sign in)
 */
export const signInWithGoogle = async (userType?: UserType) => {
  try {
    // Construct redirect URL with user_type parameter if provided
    const redirectUrl = new URL(`${window.location.origin}/auth/callback`);
    if (userType) {
      redirectUrl.searchParams.set('user_type', userType);
    }

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl.toString(),
      },
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error: unknown) {
    const errorMessage = isErrorWithMessage(error)
      ? error.message
      : 'An error occurred';
    console.error('Google sign in error:', error);
    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Handle Google OAuth callback and manage user onboarding
 */
export const handleGoogleCallback = async () => {
  try {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();

    if (error) throw error;
    if (!session?.user) {
      return {
        success: false,
        error: 'No user session found',
      };
    }

    const user = session.user;
    const userEmail = user.email;

    if (!userEmail) {
      return {
        success: false,
        error: 'No email found in Google account',
      };
    }

    // Check if this is a new user by checking if user_type exists in metadata
    const hasUserType = user.user_metadata?.user_type;

    // Get user type from URL params (passed from signup page)
    const urlParams = new URLSearchParams(window.location.search);
    const userTypeFromUrl = urlParams.get('user_type') as UserType;

    // Also check creation time as a secondary indicator (within last 5 minutes)
    const isRecentlyCreated =
      new Date(user.created_at).getTime() > Date.now() - 300000; // 5 minutes

    // If no user type in metadata but we have one from URL (signup flow)
    if (!hasUserType && userTypeFromUrl) {
      // Update user metadata immediately with the selected role
      const { error: updateError } = await supabase.auth.updateUser({
        data: {
          user_type: userTypeFromUrl,
          first_name:
            user.user_metadata.full_name?.split(' ')[0] ||
            user.user_metadata.name?.split(' ')[0] ||
            '',
          last_name:
            user.user_metadata.full_name?.split(' ').slice(1).join(' ') ||
            user.user_metadata.name?.split(' ').slice(1).join(' ') ||
            '',
        },
      });

      if (updateError) {
        console.error('Error updating user metadata:', updateError);
      }

      // Track referral with FirstPromoter for new Google signups
      if (typeof window !== 'undefined' && typeof window.fpr === 'function' && userEmail) {
        try {
          window.fpr('referral', { email: userEmail });
        } catch (e) {
          console.warn('FirstPromoter referral tracking failed:', e);
        }
      }

      // Return success with the user type from URL - NO role selection needed
      return {
        success: true,
        isNewUser: true,
        user,
        session,
        userType: userTypeFromUrl,
        requiresProfileSetup: true,
        requiresRoleSelection: false, // Explicitly set to false
      };
    }

    // If no user type and recently created, it's definitely a new user
    if (!hasUserType && isRecentlyCreated) {
      // Track referral with FirstPromoter for new Google signups
      if (typeof window !== 'undefined' && typeof window.fpr === 'function' && userEmail) {
        try {
          window.fpr('referral', { email: userEmail });
        } catch (e) {
          console.warn('FirstPromoter referral tracking failed:', e);
        }
      }

      return {
        success: true,
        requiresRoleSelection: true,
        user,
        session,
        isNewUser: true,
      };
    }

    // If no user type but not recently created, could be an existing user who signed up with Google before
    // but never completed onboarding - treat as new user needing role selection
    if (!hasUserType) {
      // Track referral with FirstPromoter (might be a returning incomplete signup)
      if (typeof window !== 'undefined' && typeof window.fpr === 'function' && userEmail) {
        try {
          window.fpr('referral', { email: userEmail });
        } catch (e) {
          console.warn('FirstPromoter referral tracking failed:', e);
        }
      }

      return {
        success: true,
        requiresRoleSelection: true,
        user,
        session,
        isNewUser: true,
      };
    }

    // User has a type - check if they have profile data
    const userType = hasUserType;
    const hasProfileData = await checkUserProfileExists(user.id, userType);

    if (!hasProfileData) {
      // Existing user with type but no profile setup
      return {
        success: true,
        isNewUser: false,
        user,
        session,
        userType,
        requiresProfileSetup: true,
        requiresRoleSelection: false,
      };
    }

    // Existing user with complete profile
    return {
      success: true,
      isNewUser: false,
      user,
      session,
      userType,
      requiresProfileSetup: false,
      requiresRoleSelection: false,
    };
  } catch (error: unknown) {
    const errorMessage = isErrorWithMessage(error)
      ? error.message
      : 'An error occurred during Google authentication';
    console.error('Google callback error:', error);
    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Sign in/up with Apple OAuth
 * This function handles both new users (sign up) and existing users (sign in)
 */
export const signInWithApple = async (userType?: UserType) => {
  try {
    // Construct redirect URL with user_type parameter if provided
    const redirectUrl = new URL(`${window.location.origin}/auth/apple-callback`);
    if (userType) {
      redirectUrl.searchParams.set('user_type', userType);
    }

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: {
        redirectTo: redirectUrl.toString(),
      },
    });

    if (error) throw error;
    return { success: true, data };
  } catch (error: unknown) {
    const errorMessage = isErrorWithMessage(error)
      ? error.message
      : 'An error occurred';
    console.error('Apple sign in error:', error);
    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Handle Apple OAuth callback and manage user onboarding
 */
export const handleAppleCallback = async () => {
  try {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();

    if (error) throw error;
    if (!session?.user) {
      return {
        success: false,
        error: 'No user session found',
      };
    }

    const user = session.user;
    const userEmail = user.email;

    if (!userEmail) {
      return {
        success: false,
        error: 'No email found in Apple account',
      };
    }

    // Check if this is a new user by checking if user_type exists in metadata
    const hasUserType = user.user_metadata?.user_type;

    // Get user type from URL params (passed from signup page)
    const urlParams = new URLSearchParams(window.location.search);
    const userTypeFromUrl = urlParams.get('user_type') as UserType;

    // Also check creation time as a secondary indicator (within last 5 minutes)
    const isRecentlyCreated =
      new Date(user.created_at).getTime() > Date.now() - 300000; // 5 minutes

    // If no user type in metadata but we have one from URL (signup flow)
    if (!hasUserType && userTypeFromUrl) {
      // Update user metadata immediately with the selected role
      const { error: updateError } = await supabase.auth.updateUser({
        data: {
          user_type: userTypeFromUrl,
          first_name:
            user.user_metadata.full_name?.split(' ')[0] ||
            user.user_metadata.name?.split(' ')[0] ||
            '',
          last_name:
            user.user_metadata.full_name?.split(' ').slice(1).join(' ') ||
            user.user_metadata.name?.split(' ').slice(1).join(' ') ||
            '',
        },
      });

      if (updateError) {
        console.error('Error updating user metadata:', updateError);
      }

      // Track referral with FirstPromoter for new Apple signups
      if (typeof window !== 'undefined' && typeof window.fpr === 'function' && userEmail) {
        try {
          window.fpr('referral', { email: userEmail });
        } catch (e) {
          console.warn('FirstPromoter referral tracking failed:', e);
        }
      }

      // Return success with the user type from URL - NO role selection needed
      return {
        success: true,
        isNewUser: true,
        user,
        session,
        userType: userTypeFromUrl,
        requiresProfileSetup: true,
        requiresRoleSelection: false, // Explicitly set to false
      };
    }

    // If no user type and recently created, it's definitely a new user
    if (!hasUserType && isRecentlyCreated) {
      // Track referral with FirstPromoter for new Apple signups
      if (typeof window !== 'undefined' && typeof window.fpr === 'function' && userEmail) {
        try {
          window.fpr('referral', { email: userEmail });
        } catch (e) {
          console.warn('FirstPromoter referral tracking failed:', e);
        }
      }

      return {
        success: true,
        requiresRoleSelection: true,
        user,
        session,
        isNewUser: true,
      };
    }

    // If no user type but not recently created, could be an existing user who signed up with Apple before
    // but never completed onboarding - treat as new user needing role selection
    if (!hasUserType) {
      // Track referral with FirstPromoter (might be a returning incomplete signup)
      if (typeof window !== 'undefined' && typeof window.fpr === 'function' && userEmail) {
        try {
          window.fpr('referral', { email: userEmail });
        } catch (e) {
          console.warn('FirstPromoter referral tracking failed:', e);
        }
      }

      return {
        success: true,
        requiresRoleSelection: true,
        user,
        session,
        isNewUser: true,
      };
    }

    // User has a type - check if they have profile data
    const userType = hasUserType;
    const hasProfileData = await checkUserProfileExists(user.id, userType);

    if (!hasProfileData) {
      // Existing user with type but no profile setup
      return {
        success: true,
        isNewUser: false,
        user,
        session,
        userType,
        requiresProfileSetup: true,
        requiresRoleSelection: false,
      };
    }

    // Existing user with complete profile
    return {
      success: true,
      isNewUser: false,
      user,
      session,
      userType,
      requiresProfileSetup: false,
      requiresRoleSelection: false,
    };
  } catch (error: unknown) {
    const errorMessage = isErrorWithMessage(error)
      ? error.message
      : 'An error occurred during Apple authentication';
    console.error('Apple callback error:', error);
    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Check if user has profile data in filmmaker/client tables
 */
const checkUserProfileExists = async (
  userId: string,
  userType?: UserType
): Promise<boolean> => {
  if (!userType) return false;

  try {
    if (userType === 'filmmaker' || userType === 'both') {
      const filmmakers = await api<any[]>(`/filmmakers?user_id=${userId}`);
      if (filmmakers && filmmakers.length > 0) {
        return true;
      }
    }

    if (userType === 'client' || userType === 'both') {
      const clients = await api<any[]>(`/clients?user_id=${userId}`);
      if (clients && clients.length > 0) {
        return true;
      }
    }

    return false;
  } catch (error) {
    console.error('Error checking user profile:', error);
    return false;
  }
};

/**
 * Check if an email already exists in the users table (for preventing account duplication)
 */
const checkExistingEmailUser = async (email: string) => {
  try {
    // Use the user-api edge function to check email
    const users = await api<any[]>(`/user-api/by-email/${encodeURIComponent(email)}`);
    
    // If no users found, return null (expected for new users)
    if (!users || users.length === 0) {
      return null;
    }

    // Return the first user found
    return users[0];
  } catch (error: any) {
    // If it's a 404, that means no user found - this is expected
    if (error.status === 404) {
      return null;
    }
    console.error('Error checking existing email user:', error);
    throw error;
  }
};

/**
 * Get user type name from user_type_id
 */
const getUserTypeName = async (
  userTypeId: number
): Promise<UserType | null> => {
  // Use hardcoded mapping to avoid database lookups
  const typeMapping: Record<number, UserType> = {
    1: 'client',
    2: 'filmmaker', // Note: database might have 'filmmker' but we normalize to 'filmmaker'
    3: 'admin',
    4: 'both',
  };

  // Return from mapping
  return typeMapping[userTypeId] || null;
};
